import type { AxiosInstance } from "axios";
import type { IFactoryFunctionRequest, IFactoryResponse, paginate } from "@bc-library/interface";
import { EMethod } from "@bc-library/interface";
import { newDateFromUTC } from "@bc-library/common";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";
import useAxios from "@/services/api/useAxios";
import type { IJobBoardFirm } from "@/services/firm/IFirms";

const resource = "firms";

const checkExists =
  (axios: IFactoryFunctionRequest) =>
  ({ firmId }: { firmId: string }): IFactoryResponse<IJobBoardFirm> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/checkId/${firmId}`,
    });
  };

const manageFirms =
  (axios: IFactoryFunctionRequest) =>
  (query: { page: number; limit: number; search?: string; clientStatus?: "active" | "former" | "all" }): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/manage`,
      query,
    });
  };

type TCreateFirmPayload = {
  firm: {
    company: string;
    corporateMail: string;
    code?: string;
    subscribingUser: string;
    accountStartedOn?: Date | string | null;
    accountBillingDate?: Date | string | null;
    billingDay?: number;
    nextVersion?: string;
  };
  user: {
    email: string;
    firstname: string;
    lastname: string;
    lang?: string;
    provider?: string;
  };
  resource: {
    firstname: string;
    lastname: string;
  };
  job: string;
  office: string;
  countryId: string;
  sendInvitation?: boolean;
};

const adminCreateFirm =
  (axios: IFactoryFunctionRequest) =>
  (data: TCreateFirmPayload): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin`,
      data,
    });
  };

const manageFirm =
  (axios: IFactoryFunctionRequest) =>
  (firmId): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}`,
    });
  };
const manageFirmConsumtion =
  (axios: IFactoryFunctionRequest) =>
  (firmId, query): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/manage/${firmId}/consumtion`,
      query,
    });
  };
const firmEditInformations = (axios: IFactoryFunctionRequest) => (): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/edit`,
  });
};

const uploadFirmLogo =
  (axios: IFactoryFunctionRequest) =>
  (logo, { key = "picture" }): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/uploadPicture`,
      data: logo,
      query: { key },
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

const adminUploadFirmLogo =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
    logo: FormData,
    { key = "picture" }: { key?: string },
  ): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/upload-logo`,
      data: logo,
      query: { key },
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

const uploadOnboardingQuotePhoto =
  (axios: IFactoryFunctionRequest) =>
  (logo): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/uploadOnboardingQuotePhoto`,
      data: logo,
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

const partialUpdate =
  (axios: IFactoryFunctionRequest) =>
  (data): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/partialUpdate`,
      data,
    });
  };

const firmSettings = (axios: IFactoryFunctionRequest) => (): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/firmSettings`,
  });
};

const firmContext = (axios: IFactoryFunctionRequest) => (): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/context`,
  });
};

const updateFirmSettings =
  (axios: IFactoryFunctionRequest) =>
  (data): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/firmSettings`,
      data,
    });
  };

// Admin-specific firm management
const adminUpdateFirm =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, data: any): IFactoryResponse<any> => {
    console.log("adminUpdateFirm", firmId, data);
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}`,
      data,
    });
  };

const adminSetDedicatedApiUrl =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, dedicatedApiUrl: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/dedicated-api-url`,
      data: { dedicatedApiUrl },
    });
  };

const adminDeleteDedicatedApiUrl =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/dedicated-api-url`,
    });
  };

const adminTestDedicatedApi =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/test-dedicated-api`,
    });
  };

// S3 Configuration for dedicated servers
const adminSetS3Config =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
    config: {
      bucket: string;
      region: string;
      endpoint?: string;
      accessKey: string;
      secretKey: string;
    },
  ): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/s3-config`,
      data: config,
    });
  };

const adminRemoveS3Config =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/s3-config`,
    });
  };

const adminDeleteFirm =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, data: { code: string }): IFactoryResponse<any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}`,
      data,
    });
  };

const adminSendFirmPurgeCode =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
  ): IFactoryResponse<{ success: boolean; sentTo?: string; expiresInSeconds?: number }> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/purge-code`,
    });
  };

// Module listing
const adminListModules = (axios: IFactoryFunctionRequest) => (): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/modules/list`,
  });
};

const adminLookupUsers =
  (axios: IFactoryFunctionRequest) =>
  (query: {
    q?: string;
    userId?: string;
    resourceId?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
    firmId?: string;
    dedicatedOnly?: boolean;
  }): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/lookup`,
      query,
    });
  };

export type TFirmUserProfile = {
  _id: string;
  display: string;
};

export type TFirmUserJob = {
  _id?: string;
  positionId?: string | null;
  officeId?: string | null;
  entityId?: string | null;
  managerId?: string | null;
  managerId2?: string | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  isAlumni?: boolean;
};

export type TFirmUserSession = {
  _id: string;
  createdAt?: string | Date | null;
  lastActivityAt?: string | Date | null;
  expiresAt?: string | Date | null;
  isRevoked: boolean;
  isExpired: boolean;
  ip?: string | null;
  provider: string;
  browser: string;
  os: string;
  deviceType: string;
};

export type TFirmUserModuleUsage = {
  module: string;
  days: number;
  lastSeen?: string | Date | null;
};

export type TFirmUser = {
  _id: string;
  firstname?: string;
  lastname?: string;
  picture?: unknown;
  role: string;
  isActive: number;
  recruitmentDate?: string | Date | null;
  departureDate?: string | Date | null;
  lastConnection?: string | Date | null;
  userId?: string | null;
  email?: string | null;
  lang?: string | null;
  isActivated?: boolean | null;
  userRole?: string;
  accessProfile?: string | null;
  profile?: TFirmUserProfile | null;
  availableProfiles?: TFirmUserProfile[];
  office?: string | null;
  job?: string | null;
  lastActivityAt?: string | Date | null;
  loginsPeriod: number;
  activeSessions: number;
  onlineNow: boolean;
  // Detail sheet only
  username?: string | null;
  resourceType?: number;
  isOnboarded?: boolean;
  createdAt?: string | Date | null;
  userCreatedAt?: string | Date | null;
  toChangePassword?: boolean | null;
  pwdErrors?: number;
  jobs?: TFirmUserJob[];
  sessions?: TFirmUserSession[];
  moduleUsage?: TFirmUserModuleUsage[];
};

export type TFirmUsersPage = {
  docs: TFirmUser[];
  count: {
    total: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    limit: number;
  };
  activityDays: number;
};

export type TFirmUserDetail = {
  user: TFirmUser;
  profiles: TFirmUserProfile[];
  positions: TFirmUserProfile[];
  offices: TFirmUserProfile[];
  managers: TFirmUserProfile[];
  activityDays: number;
  revokedCount?: number;
};

const hydrateFirmUser = (user: TFirmUser): TFirmUser => ({
  ...user,
  recruitmentDate: user.recruitmentDate ? newDateFromUTC(user.recruitmentDate) : null,
  departureDate: user.departureDate ? newDateFromUTC(user.departureDate) : null,
  lastConnection: user.lastConnection ? new Date(user.lastConnection) : null,
  lastActivityAt: user.lastActivityAt ? new Date(user.lastActivityAt) : null,
  ...(user.jobs
    ? {
        jobs: user.jobs.map((job) => ({
          ...job,
          startDate: job.startDate ? newDateFromUTC(job.startDate) : null,
          endDate: job.endDate ? newDateFromUTC(job.endDate) : null,
        })),
      }
    : {}),
});

const afterReceivingFirmUsersPage = (data: TFirmUsersPage): TFirmUsersPage => ({
  ...data,
  docs: (data?.docs || []).map(hydrateFirmUser),
});

const afterReceivingFirmUserDetail = (data: TFirmUserDetail): TFirmUserDetail => ({
  ...data,
  user: data?.user ? hydrateFirmUser(data.user) : data.user,
});

const adminListFirmUsers =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
    query: {
      page?: number;
      limit?: number;
      search?: string;
      status?: "present" | "alumni" | "all";
      role?: "resource" | "client_admin";
    },
  ): IFactoryResponse<TFirmUsersPage> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/users`,
      query,
      afterReceivingHook: afterReceivingFirmUsersPage,
    });
  };

const adminGetFirmUser =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, resourceId: string): IFactoryResponse<TFirmUserDetail> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/users/${resourceId}`,
      afterReceivingHook: afterReceivingFirmUserDetail,
    });
  };

const adminUpdateFirmUser =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
    resourceId: string,
    data: {
      role?: "resource" | "client_admin";
      accessProfile?: string | null;
      availableProfileIds?: string[];
      firstname?: string;
      lastname?: string;
      resourceType?: number;
      email?: string;
      lang?: string;
      isActivated?: boolean;
      toChangePassword?: boolean;
      jobs?: TFirmUserJob[];
    },
  ): IFactoryResponse<TFirmUserDetail> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/users/${resourceId}`,
      data,
      afterReceivingHook: afterReceivingFirmUserDetail,
    });
  };

const adminRevokeFirmUserSessions =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, resourceId: string): IFactoryResponse<TFirmUserDetail> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/users/${resourceId}/revoke-sessions`,
      afterReceivingHook: afterReceivingFirmUserDetail,
    });
  };

type TFirmProfileQuery = { page?: number; limit?: number; display?: string; search?: string };

const adminListFirmProfiles =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, query: TFirmProfileQuery): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles`,
      query,
    });
  };

const adminGetFirmProfile =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, profileId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles/${profileId}`,
    });
  };

const adminUpdateFirmProfile =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, profileId: string, data: Record<string, unknown>): IFactoryResponse<any> => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles/${profileId}`,
      data,
    });
  };

const adminDeleteFirmProfile =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, profileId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles/${profileId}`,
    });
  };

const adminCreateFirmProfile =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, data: { display?: string; preset?: string } = {}): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles`,
      data,
    });
  };

const adminDuplicateFirmProfile =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, profileId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles/${profileId}/duplicate`,
    });
  };

const adminBatchUpdateFirmProfiles =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
    data: { profileIds: string[]; updates: Record<string, boolean | string> },
  ): IFactoryResponse<{ updated: number; matched: number }> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles/batch-update-modules`,
      data,
    });
  };

const adminListFirmProfileResources =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
    profileId: string,
    query: { page?: number; limit?: number; search?: string },
  ): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles/${profileId}/resources`,
      query,
    });
  };

const adminCountFirmProfileResources =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, profileId: string): IFactoryResponse<{ count: number }> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/profiles/${profileId}/count`,
    });
  };

const adminFilterFirmOffices =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, query: TFirmProfileQuery): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/offices`,
      query,
    });
  };

const adminFilterFirmInvoicing =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, query: TFirmProfileQuery): IFactoryResponse<any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/invoicing-informations`,
      query,
    });
  };

// Subscription management
const adminAddSubscription =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, moduleId: number): IFactoryResponse<any> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/subscriptions`,
      data: { moduleId },
    });
  };

const adminDeactivateSubscription =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, subscriptionId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/subscriptions/${subscriptionId}/deactivate`,
    });
  };

const adminActivateSubscription =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, subscriptionId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/subscriptions/${subscriptionId}/activate`,
    });
  };

const adminDeleteSubscription =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, subscriptionId: string): IFactoryResponse<any> => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/subscriptions/${subscriptionId}`,
    });
  };

const adminUpdateSubscriptionAmount =
  (axios: IFactoryFunctionRequest) =>
  (firmId: string, subscriptionId: string, defaultAmount: number): IFactoryResponse<any> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/subscriptions/${subscriptionId}/amount`,
      data: { defaultAmount },
    });
  };

const adminUpdateSubscriptionDate =
  (axios: IFactoryFunctionRequest) =>
  (
    firmId: string,
    subscriptionId: string,
    dateTermination: string | null,
  ): IFactoryResponse<any> => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${firmId}/subscriptions/${subscriptionId}/date`,
      data: { dateTermination },
    });
  };

export default (axios: AxiosInstance) => ({
  checkExists: checkExists(useAxios(axios)),
  manageFirms: manageFirms(useAxios(axios)),
  manageFirm: manageFirm(useAxios(axios)),
  manageFirmConsumtion: manageFirmConsumtion(useAxios(axios)),
  firmEditInformations: firmEditInformations(useAxios(axios)),
  uploadFirmLogo: uploadFirmLogo(useAxios(axios)),
  adminUploadFirmLogo: adminUploadFirmLogo(useAxios(axios)),
  uploadOnboardingQuotePhoto: uploadOnboardingQuotePhoto(useAxios(axios)),
  partialUpdate: partialUpdate(useAxios(axios)),
  firmSettings: firmSettings(useAxios(axios)),
  firmContext: firmContext(useAxios(axios)),
  updateFirmSettings: updateFirmSettings(useAxios(axios)),
  adminCreateFirm: adminCreateFirm(useAxios(axios)),
  adminUpdateFirm: adminUpdateFirm(useAxios(axios)),
  adminSetDedicatedApiUrl: adminSetDedicatedApiUrl(useAxios(axios)),
  adminDeleteDedicatedApiUrl: adminDeleteDedicatedApiUrl(useAxios(axios)),
  adminTestDedicatedApi: adminTestDedicatedApi(useAxios(axios)),
  adminSetS3Config: adminSetS3Config(useAxios(axios)),
  adminRemoveS3Config: adminRemoveS3Config(useAxios(axios)),
  adminDeleteFirm: adminDeleteFirm(useAxios(axios)),
  adminSendFirmPurgeCode: adminSendFirmPurgeCode(useAxios(axios)),
  adminListModules: adminListModules(useAxios(axios)),
  adminLookupUsers: adminLookupUsers(useAxios(axios)),
  adminListFirmUsers: adminListFirmUsers(useAxios(axios)),
  adminGetFirmUser: adminGetFirmUser(useAxios(axios)),
  adminUpdateFirmUser: adminUpdateFirmUser(useAxios(axios)),
  adminRevokeFirmUserSessions: adminRevokeFirmUserSessions(useAxios(axios)),
  adminListFirmProfiles: adminListFirmProfiles(useAxios(axios)),
  adminGetFirmProfile: adminGetFirmProfile(useAxios(axios)),
  adminUpdateFirmProfile: adminUpdateFirmProfile(useAxios(axios)),
  adminDeleteFirmProfile: adminDeleteFirmProfile(useAxios(axios)),
  adminCreateFirmProfile: adminCreateFirmProfile(useAxios(axios)),
  adminDuplicateFirmProfile: adminDuplicateFirmProfile(useAxios(axios)),
  adminBatchUpdateFirmProfiles: adminBatchUpdateFirmProfiles(useAxios(axios)),
  adminListFirmProfileResources: adminListFirmProfileResources(useAxios(axios)),
  adminCountFirmProfileResources: adminCountFirmProfileResources(useAxios(axios)),
  adminFilterFirmOffices: adminFilterFirmOffices(useAxios(axios)),
  adminFilterFirmInvoicing: adminFilterFirmInvoicing(useAxios(axios)),
  adminAddSubscription: adminAddSubscription(useAxios(axios)),
  adminDeactivateSubscription: adminDeactivateSubscription(useAxios(axios)),
  adminActivateSubscription: adminActivateSubscription(useAxios(axios)),
  adminDeleteSubscription: adminDeleteSubscription(useAxios(axios)),
  adminUpdateSubscriptionAmount: adminUpdateSubscriptionAmount(useAxios(axios)),
  adminUpdateSubscriptionDate: adminUpdateSubscriptionDate(useAxios(axios)),
});
